import { useEffect, useState } from 'react'
import type { FC } from 'react'
import { useUserSession, useNotification, useCart, useAfiliado } from 'hooks'
import parser from 'fazendinha/user'
import { withRouter } from 'next/router'
import { UserService } from 'modules/users/services'
import { TimerRedirectTo } from 'components/timers'
import { ROUTE_INDEX } from 'routes/home'
import {
  ROUTE_USER_CHANGE_PASSWORD,
  ROUTE_USER_RECOVER_PASSWORD,
  ROUTE_USER_RECOVER_PASSWORD_VERIFY,
  ROUTE_USER_SIGN_IN,
  ROUTE_USER_SIGN_UP,
} from 'routes/user'
import { SignIn } from 'modules/users/components'
import { UserEmail } from 'framework/users'
import type { UserEntryProps, UserSessionPath } from '.'
import { RecoverPasswordRequest, RecoverPasswordValidation, UserChangePassword, UserCreate } from '..'
import { AddressService } from 'modules/address/services'
import { setCookie } from 'nookies'

const UserEntry: FC<UserEntryProps> = ({ router }) => {
  const path = router.asPath

  const { setUser, user } = useUserSession()
  const { setZipCode } = useCart()
  const { setIsAfiliado, setAfiliadoSlug } = useAfiliado();

  const { notify } = useNotification()
  const [form, setForm] = useState<UserSessionPath>(ROUTE_USER_SIGN_IN)
  const [redirectTo, setRedirectTo] = useState<string>(null as unknown as string)
  const [isSubscription, setSubscription] = useState<boolean>(false as unknown as boolean)
  const [userEmail, setUserEmail] = useState<UserEmail>(null as unknown as UserEmail)
  const [forceChangePassword, setForceChangePassword] = useState<string>('')
  const addressService = new AddressService();

  const { query } = router

  useEffect(() => {
    setForm(path as UserSessionPath)
    if (query?.from) {
      setRedirectTo(query?.from as unknown as string)
    }
    if (query?.subscription) {
      setSubscription(query?.subscription as unknown as boolean)
    }
  }, [path, query])

  function redirect() {
    if (redirectTo) {
      if (redirectTo === '/checkout' && isSubscription) {
        router.push({
          pathname: redirectTo,
          query: { subscription: isSubscription }
        })
      } else {
        router.push(redirectTo)
      }
    }
  }

  function handleForm(value: UserSessionPath) {
    setForm(value)
  }

  async function getUserAddress(userData: any) {
    const service = new UserService()
    // @ts-ignore
    const adsResponse = await service.setToken(userData.token).getAddresses(userData.id)
    // @ts-ignore
    const { addresses } = adsResponse.data
    if (!addresses) return

    const addressForZip = addresses.shipping_address?.[0] ?? addresses.billing_address ?? null

    if (addressForZip) {
      const value = addressForZip.cep.replace(/\D/g, '')
      try {
        const { data } = await addressService.getAddressByZipcode(value)
        setCookie(null, 'city', data.city_name, {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        })
        setCookie(null, 'zipcode', value, {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        })
        setZipCode(value)
      } catch {
        // viacep indisponível: mantém o zipCode atual sem exibir aviso
      }
    }
  }

  async function handleSingIn(userData: any) {
    let u = null;
    try {
      const service = new UserService()
      const { data } = await service.auth(userData.email, userData.password)
      notify({
        title: 'Autenticado com sucesso',
        type: 'success',
      })

      if (data.isAfiliado === true) {
        setIsAfiliado(true)
        setAfiliadoSlug(data.afiliadoSlug)
      }
      u = parser(data)
      setUser(u)

      if (redirectTo) {
        if (redirectTo === '/checkout' && isSubscription) {
          router.push({
            pathname: redirectTo,
            query: { subscription: isSubscription }
          })
        } else {
          router.push(redirectTo)
        }
      }
    } catch (e) {
      setForceChangePassword(userData.email)
      notify({
        title: 'Atenção',
        description: 'E-mail ou senha inválido',
        type: 'danger',
      })
    }

    if (u) {
      try {
        await getUserAddress(u);
      } catch (error) {
        notify({
          title: 'Aviso',
          description: 'Não foi possível carregar seu endereço salvo.',
          type: 'warning',
        })
      }
    }
  }

  function onRedirect() {
    if (redirectTo) {
      if (redirectTo === '/checkout' && isSubscription) {
        router.push({
          pathname: redirectTo,
          query: { subscription: isSubscription }
        })
      } else {
        router.push(redirectTo)
      }
      return
    }

    router.push(ROUTE_INDEX)
  }

  function hasCodeSended(email: UserEmail) {
    setUserEmail(email)
    handleForm(ROUTE_USER_RECOVER_PASSWORD_VERIFY)
  }

  function hasUserCreated() {
    redirect()
  }

  function hasValidated() {
    handleForm(ROUTE_USER_CHANGE_PASSWORD)
  }

  function hasPasswordChanged() {
    handleForm(ROUTE_USER_SIGN_IN)
  }

  useEffect(() => {
    console.log("forceChangePassword: ", forceChangePassword)
    if (forceChangePassword !== '') {
      handleForm(ROUTE_USER_RECOVER_PASSWORD)
    }
  }, [forceChangePassword])

  switch (form) {
    default:
    case ROUTE_USER_SIGN_IN:
      if (user?.id) {
        return <TimerRedirectTo onFinish={onRedirect} showButton />
      }

      return (
        <SignIn
          pushToSingUp={() => handleForm(ROUTE_USER_SIGN_UP)}
          pushToRecoveryRequest={() => handleForm(ROUTE_USER_RECOVER_PASSWORD)}
          onSubmit={handleSingIn}
        />
      )
    case ROUTE_USER_SIGN_UP:
      if (user?.id) {
        return <TimerRedirectTo onFinish={onRedirect} showButton />
      }

      return <UserCreate onCancel={() => handleForm(ROUTE_USER_SIGN_IN)} hasUserCreated={hasUserCreated} />
    case ROUTE_USER_RECOVER_PASSWORD:
      return (
        <RecoverPasswordRequest
          forceChangePassword={forceChangePassword}
          onCancel={() => handleForm(ROUTE_USER_SIGN_IN)}
          hasCodeSended={hasCodeSended}
        />
      )
    case ROUTE_USER_RECOVER_PASSWORD_VERIFY:
      return (
        <RecoverPasswordValidation
          userEmail={userEmail || ''}
          onCancel={() => handleForm(ROUTE_USER_RECOVER_PASSWORD)}
          hasValidated={hasValidated}
        />
      )
    case ROUTE_USER_CHANGE_PASSWORD:
      return <UserChangePassword userEmail={userEmail} hasPasswordChanged={hasPasswordChanged} />
  }
}

export default withRouter(UserEntry)
